import { PortOrAddress } from 'features/Directory'
import { ShipmentInterface, ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { compact, every, find, values } from 'lodash-es'

import { isFak } from './QuoteSheetModel'
import { IQuoteOption } from './QuoteSheetOption'

export interface IBookingDetail {
  mode: ShipmentMode
  type: ShipmentType
  mainCarriageOrigin: PortOrAddress
  mainCarriageDestination: PortOrAddress
  isFak: boolean
}

type Term = 'import_specific' | 'export_specific' | 'general'

type Predicate = (data: IBookingDetail) => boolean

const mainRateIsFAK: Predicate = (data) => data.isFak
const isOfType =
  (type: ShipmentType): Predicate =>
  (data) =>
    data.type === type
const isOfMode =
  (mode: ShipmentMode): Predicate =>
  (data) =>
    data.mode === mode
const podIsOneOf =
  (portIds: string[]): Predicate =>
  (data) =>
    portIds.indexOf(data.mainCarriageDestination._id) >= 0
const polIsOneOf =
  (portIds: string[]): Predicate =>
  (data) =>
    portIds.indexOf(data.mainCarriageOrigin._id) >= 0

interface TermPredicate {
  term: Term
  predicates: Predicate[]
}

// Predicates to select the term.
// See: https://docs.google.com/document/d/1VGyqs2c3FlTc_vsIdMKcsTsbSdxFInfBuxrKgYc6AOw/edit?usp=sharing
const termPredicates: TermPredicate[] = [
  {
    term: 'import_specific',
    predicates: [
      // For specific shipments imported to selected ports
      mainRateIsFAK,
      isOfMode('sea'),
      isOfType('fcl'),
      podIsOneOf(['DEBRV', 'DEHAM', 'DEWVN', 'NLRTM', 'BEANR']),
    ],
  },

  {
    term: 'export_specific',
    predicates: [
      // For specific shipments exported from selected ports
      mainRateIsFAK,
      isOfMode('sea'),
      isOfType('fcl'),
      polIsOneOf(['DEBRV', 'DEHAM', 'DEWVN', 'NLRTM', 'BEANR']),
    ],
  },

  {
    term: 'general',
    predicates: [
      // For all the rest of shipments.
      // No predicates means this will always be true, so should always be last.
    ],
  },
]

export function fromQuoteOption(option: IQuoteOption): IBookingDetail {
  const { origin, destination } = option.routeQuotes.mainQuote.route.journey

  return {
    isFak: !option.isNotFak,
    mainCarriageOrigin: origin,
    mainCarriageDestination: destination,
    mode: option.mode,
    type: option.type,
  }
}

function fromShipment(shipment: ShipmentInterface): IBookingDetail {
  const { origin, destination } = shipment.quoteOption.routeQuotes.mainQuote.route.journey

  return {
    isFak: isFak(compact(values(shipment.quoteOption.routeQuotes))),
    mainCarriageOrigin: origin,
    mainCarriageDestination: destination,
    mode: shipment.mode,
    type: shipment.type,
  }
}

export function getTerm(data: IBookingDetail): Term | null {
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  return find(termPredicates, ({ predicates }) => every(predicates, (predicate) => predicate(data))).term || null
}

export const getTermFromShipment = (shipment: ShipmentInterface): Term | null => getTerm(fromShipment(shipment))
